// Thin fetch wrapper for /api/v2. Sends the CSRF header the server requires on cookie-authenticated writes.
import { useCallback, useEffect, useRef, useState } from "react";

export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string, public body?: any) {
    super(message);
  }
}

/** Called on any 401 so the auth context can drop the signed-in user. */
let onUnauthorized: (() => void) | null = null;
export const setUnauthorizedHandler = (fn: (() => void) | null) => { onUnauthorized = fn; };

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/v2${path}`, {
    method,
    credentials: "same-origin",
    headers: { "X-Requested-With": "exoware", ...(body !== undefined ? { "Content-Type": "application/json" } : {}) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  let data: any = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith("/auth/")) onUnauthorized?.();
    throw new ApiError(res.status, data?.error ?? `Request failed (${res.status})`, data?.code, data);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body: unknown = {}) => request<T>("POST", path, body),
  put: <T>(path: string, body: unknown = {}) => request<T>("PUT", path, body),
  patch: <T>(path: string, body: unknown = {}) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};

export interface Resource<T> {
  data: T | undefined;
  error: ApiError | null;
  loading: boolean;
  /** Refetch without clearing the current data. */
  reload: () => Promise<void>;
  /** Replace the data locally, e.g. with a mutation response. */
  setData: (d: T | ((prev: T | undefined) => T)) => void;
}

/** Fetches `path` (GET) on mount and whenever it changes. Pass null to skip. */
export function useApi<T>(path: string | null): Resource<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const latest = useRef(path);
  latest.current = path;

  const load = useCallback(async () => {
    if (path === null) return;
    setLoading(true);
    try {
      const d = await api.get<T>(path);
      if (latest.current === path) { setData(d); setError(null); }
    } catch (e) {
      if (latest.current === path) setError(e instanceof ApiError ? e : new ApiError(0, String(e)));
    } finally {
      if (latest.current === path) setLoading(false);
    }
  }, [path]);

  useEffect(() => { void load(); }, [load]);

  return { data, error, loading, reload: load, setData: setData as Resource<T>["setData"] };
}

/** Wraps an async action with pending/error state. `run` resolves to the result, or undefined on error. */
export function useAction<A extends unknown[], R>(fn: (...args: A) => Promise<R>) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(async (...args: A): Promise<R | undefined> => {
    setPending(true);
    setError(null);
    try { return await fn(...args); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); return undefined; }
    finally { setPending(false); }
  }, [fn]);
  return { run, pending, error, setError };
}
